import {
  Currencies,
  Exchanges,
  SubAccountStates,
  SubTradeRequestStates,
  SubTradeRequestTypes,
} from '@generated/graphql';
import type { HoldingReport } from '@interfaces/holdingReport';
import type {
  OptimalHolding,
  OptimalSubAccount,
} from '@interfaces/optimalPortfolio';
import type { FxRateDocument } from '@interfaces/portfolioReport';
import type { TranslatedString } from '@interfaces/shared';
import type { SubAccountReport } from '@interfaces/subAccountReport';
import type { SubTradeRequest } from '@interfaces/subTradeRequest';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { dollars2cents } from '@utils/dollars2cents';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import { round } from 'lodash-es';
import type {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from './interfaces';
import type { TradeToVerify } from './subTradeRequestsTable';
export type PortfolioObjectFilter =
  | { goalId: string }
  | { subAccountId: string }
  | { householdClientGroupId: string };

function buildObjectFilter(
  type: PageObjectType,
  id: string,
): PortfolioObjectFilter {
  if (type === PageObjectType.GOAL) return { goalId: id };
  if (type === PageObjectType.HOUSEHOLD) return { householdClientGroupId: id };
  // ACCOUNT, SUB_ACCOUNT, INDIVIDUAL, NON_INDIVIDUAL, ACCOUNT_GROUP, etc.
  return { subAccountId: id };
}

/**
 * Report fetch scoping. HOUSEHOLD reports are keyed on the ClientGroup id and
 * resolved via FetchPortfolioReportsInput.filter.householdClientGroupId (org
 * schema → buildPortfolioReportFilterParams sets modelPortfolioType=HOUSEHOLD).
 */
export const buildPortfolioObjectFilter = (
  type: PageObjectType,
  id: string,
): PortfolioObjectFilter => buildObjectFilter(type, id);

/**
 * Sub-trade fetch scoping. Org `SubTradeRequestQueryFilter` (trading-service)
 * accepts `householdClientGroupId`, mapped to `SubTradeRequest.householdClientGroup`.
 */
export const buildSubTradeObjectFilter = (
  type: PageObjectType,
  id: string,
): PortfolioObjectFilter => buildObjectFilter(type, id);

export const PORTFOLIO_ROW_HEIGHT = {
  standard: '42px',
  extended: '50px',
} as const;

export const PORTFOLIO_CELL_HEIGHT = {
  standard: '42px',
  extended: '50px',
  cash: '40px',
} as const;

export const PORTFOLIO_COLUMN_WIDTH = {
  expand: '32px',
  assetGroup: '80px',
  security: '60px',
} as const;

const PORTFOLIO_MEMBER_NAME_MIN_PX = 130;
/** Flag + "$ 123,123.12" + trade indicator — holding inputs need at least this width. */
const PORTFOLIO_MEMBER_HOLDING_MIN_PX = 182;
const PORTFOLIO_MEMBER_COLUMN_MAX_PX = 210;
/** Trade column beside custom-target — input + tooltip only (no 182px holding budget). */
const PORTFOLIO_PAIRED_HOLDING_MAX_PX = 148;

/** Shared width for header, cash row, holding cells, and totals in a member column. */
export const getPortfolioMemberColumnWidthPx = (
  subAccountName: string,
): number =>
  Math.min(
    Math.max(
      subAccountName.length * 8,
      PORTFOLIO_MEMBER_NAME_MIN_PX,
      PORTFOLIO_MEMBER_HOLDING_MIN_PX,
    ),
    PORTFOLIO_MEMBER_COLUMN_MAX_PX,
  );

/** Narrower trade column when a custom-target column is adjacent. */
export const getPortfolioPairedHoldingColumnWidthPx = (
  subAccountName: string,
): number =>
  Math.min(
    Math.max(subAccountName.length * 8, PORTFOLIO_MEMBER_NAME_MIN_PX),
    PORTFOLIO_PAIRED_HOLDING_MAX_PX,
  );

export const getPortfolioMemberColumnSx = (subAccountName: string) => {
  const widthPx = getPortfolioMemberColumnWidthPx(subAccountName);
  return {
    width: `${widthPx}px`,
    minWidth: `${widthPx}px`,
    whiteSpace: 'nowrap' as const,
  };
};

export const getPortfolioPairedMemberColumnSx = (subAccountName: string) => {
  const widthPx = getPortfolioPairedHoldingColumnWidthPx(subAccountName);
  return {
    width: `${widthPx}px`,
    minWidth: `${widthPx}px`,
    whiteSpace: 'nowrap' as const,
  };
};

/** Holding row beside a custom-target column — trim right padding. */
export const getPortfolioHoldingCellSx = (subAccountName: string) => ({
  ...getPortfolioPairedMemberColumnSx(subAccountName),
  pr: 0.5,
});

/** Dollar-only custom target inputs — no flag or trade indicator. */
const PORTFOLIO_CUSTOM_TARGET_COLUMN_WIDTH_PX = 124;

export const getPortfolioCustomTargetColumnSx = () => ({
  width: `${PORTFOLIO_CUSTOM_TARGET_COLUMN_WIDTH_PX}px`,
  minWidth: `${PORTFOLIO_CUSTOM_TARGET_COLUMN_WIDTH_PX}px`,
  whiteSpace: 'nowrap' as const,
  pl: 0.5,
  pr: 1,
});

export const getPortfolioCustomTargetHeadColumnSx = () => ({
  width: `${PORTFOLIO_CUSTOM_TARGET_COLUMN_WIDTH_PX}px`,
  minWidth: `${PORTFOLIO_CUSTOM_TARGET_COLUMN_WIDTH_PX}px`,
  whiteSpace: 'nowrap' as const,
  pl: 1,
});

/** Prevents paired member/custom-target columns from auto-sizing to content. */
export const PORTFOLIO_ALLOCATION_EDITOR_TABLE_SX = {
  tableLayout: 'fixed' as const,
};

export const isSecurityHeldInSubAccount = (
  subAccount: PortfolioSubAccount,
  financialProductId: string,
): boolean =>
  subAccount.holdings?.some(
    (holding) => holding.financialProduct?.id === financialProductId,
  ) ?? false;

export type PortfolioAssetClassGroup = {
  assetClassId: string;
  name: string;
  groupPct: number;
  items: PortfolioSecurity[];
};

export const buildPortfolioSubAccount = (
  subAccountReport: SubAccountReport,
  t: TFunction,
): PortfolioSubAccount => {
  const { subAccount } = subAccountReport;
  const userName =
    subAccount.account.user.firstName ?? subAccount.account.user.entityName;
  const name = `${userName}'s ${t(`accountTypes:${subAccount.account.type}`)}`;

  return {
    id: subAccount.id,
    name,
    type: subAccount.account.type,
    taxRank: subAccount.account.taxRank ?? 0,
    holdings: subAccountReport.holdings,
    inactive: subAccount.state === SubAccountStates.Inactive,
    marketValueCents: subAccountReport.marketValueCents,
    expectedCashCents: subAccountReport.expectedCashCents,
    cashCents: subAccountReport.cashCents,
    pendingWithdrawCents: subAccountReport.pendingWithdrawCents,
    pendingContributionCents: subAccountReport.pendingContributionCents,
    pendingCashCents: subAccountReport.pendingCashCents ?? 0,
    cashOnHoldToTradeCents: subAccountReport.cashOnHoldToTradeCents,
    state: subAccount.state,
    currency: subAccountReport.currency,
    accountBaseCurrency: subAccountReport.accountBaseCurrency,
    originalValues: subAccountReport?.originalValues?.map((originalValue) => ({
      currency: originalValue.currency,
      marketValueCents: originalValue.marketValueCents,
      expectedCashCents: originalValue.expectedCashCents,
      cashCents: originalValue.cashCents,
      pendingWithdrawCents: originalValue.pendingWithdrawCents,
      pendingContributionCents: originalValue.pendingContributionCents,
      pendingCashCents: originalValue.pendingCashCents ?? 0,
      cashOnHoldToTradeCents: originalValue.cashOnHoldToTradeCents,
    })),
  };
};

/**
 * Matches the cash-equivalent check the locked-matrix solver uses
 * (useCustomAllocation's isCashHolding) so the custom-target column never
 * shows an editable input for a row the solver ignores.
 */
export const isCashSecurity = (
  security: Pick<PortfolioSecurity, 'id' | 'isCashEquivalent'>,
): boolean => security.id === 'CASH' || security.isCashEquivalent === true;

export const buildSecurity = (
  translateBackendString: (translatedObject: unknown) => string,
  security: HoldingReport,
  subAccounts: PortfolioSubAccount[],
): PortfolioSecurity => {
  const { financialProduct } = security;

  return {
    id: financialProduct?.id ?? '',
    ticker: financialProduct?.ticker ?? '',
    isCashEquivalent:
      security.isCashEquivalent === true || financialProduct?.isCash === true,
    expectedValueCents: security.expectedValueCents,
    expectedPercentage: security.expectedPercentage,
    currentPriceCents: financialProduct?.currentPriceCents ?? 0,
    translatedName:
      translateBackendString(financialProduct?.translatedName) || '',
    taxRank: security.taxRank || null,
    financialProductTaxRank: financialProduct?.taxRank ?? 0,
    settlementDays: financialProduct?.settlementDays,
    valueCents: security.valueCents,
    quantity: security.quantity,
    pendingQuantity: security.pendingQuantity ?? 0,
    subAccounts,
    currency: security.currency,
    originalValues: security?.originalValues?.map((originalValue) => ({
      currency: originalValue.currency,
      expectedValueCents: originalValue.expectedValueCents,
      expectedPercentage: originalValue.expectedPercentage,
      valueCents: originalValue.valueCents,
      quantity: originalValue.quantity,
      pendingQuantity: originalValue.pendingQuantity,
    })),
    originalCurrency: financialProduct?.originalCurrency,
    originalCurrentPriceCents: financialProduct?.originalCurrentPriceCents,
    ...(security.assetClass !== undefined && {
      assetClass: security.assetClass,
    }),
    ...(security.assetClassPercentage !== undefined && {
      assetClassPercentage: security.assetClassPercentage,
    }),
    ...(security.assetClassGroupPercentage !== undefined && {
      assetClassGroupPercentage: security.assetClassGroupPercentage,
    }),
    // internalExpectedValueCents is only populated on top-level portfolioReport.holdings
    // (not on subAccounts[].holdings). `security` here is always the top-level holding.
    ...(security.internalExpectedValueCents !== undefined && {
      internalExpectedValueCents: security.internalExpectedValueCents,
    }),
    ...(security.customExpectedPercentage !== undefined && {
      customExpectedPercentage: security.customExpectedPercentage,
    }),
  };
};

export const buildDraftSubTradeRequest = (
  subTradeRequest: SubTradeRequest,
  t: TFunction,
): DraftSubTradeRequest => ({
  id: subTradeRequest.id,
  financialProductId: subTradeRequest?.financialProduct?.id ?? '',
  isSellAll: subTradeRequest.isSellAll,
  subAccountId: subTradeRequest?.subAccount?.id ?? '',
  amountCents:
    (subTradeRequest?.preSplitMoneyAllocatedCents ??
      subTradeRequest.moneyAllocatedCents) *
    (subTradeRequest.type === SubTradeRequestTypes.Sell ? -1 : 1),
  currency: subTradeRequest.currency ?? CurrencyCodes.CAD,
  type: subTradeRequest.type,
  state: subTradeRequest.state,
  ticker: subTradeRequest?.financialProduct?.ticker ?? '',
  accountName: `${subTradeRequest?.subAccount?.account?.user?.firstName}'s ${t(`accountTypes:${subTradeRequest?.subAccount?.account?.type}`)}`,
});

export const buildDraftTrade = ({
  id,
  financialProductId,
  subAccountId,
  amountCents,
  currency,
  type,
  isSellAll,
  maxQuantity,
  ticker,
  accountName,
}: DraftSubTradeRequest): DraftSubTradeRequest => ({
  ...(id && { id }),
  financialProductId,
  subAccountId,
  amountCents,
  currency,
  type,
  isSellAll,
  ...(type === SubTradeRequestTypes.Sell && { maxQuantity }),
  ticker,
  accountName,
  isTemp: true,
});

const getAmountCents = (newAmountCents: number, currentAmountCents: number) =>
  round(newAmountCents - currentAmountCents, 2);

const getTradeType = (newAmountCents: number, currentAmountCents: number) =>
  newAmountCents > currentAmountCents
    ? SubTradeRequestTypes.Buy
    : SubTradeRequestTypes.Sell;

const checkIsSellAll = (newAmountCents: number, currentAmountCents: number) =>
  newAmountCents === 0 && currentAmountCents >= 0;

const checkIsSellAllOptimal = (
  newAmountCents: number,
  currentAmountCents: number,
) => newAmountCents < 0 && currentAmountCents * -1 === newAmountCents;

export const getIsMultiCurrencySubAccount = (
  subAccount: PortfolioSubAccount,
): boolean => (subAccount.originalValues?.length ?? 0) > 1;

/**
 * Returns the current holding value in the sub-account's native currency.
 * holding.valueCents is converted to the portfolio base currency, so we
 * prefer originalValues when available.
 */
export const getHoldingCurrentValueCents = (
  subAccount: PortfolioSubAccount,
): number => {
  if (getIsMultiCurrencySubAccount(subAccount)) {
    return (
      subAccount.holding?.originalValues?.find(
        (ov) =>
          ov.currency ===
          subAccount.holding?.financialProduct?.originalCurrency,
      )?.valueCents ?? 0
    );
  }

  return (
    subAccount.holding?.originalValues?.find(
      (ov) => ov.currency === subAccount.accountBaseCurrency,
    )?.valueCents ??
    subAccount.holding?.valueCents ??
    0
  );
};

/**
 * Returns the "Original" (pre-pending-trade) holding value shown in the
 * blue-dot tooltip, in the same currency as getHoldingCurrentValueCents.
 */
export const getHoldingOriginalValueCents = (
  subAccount: PortfolioSubAccount,
  security?: Pick<
    PortfolioSecurity,
    'currentPriceCents' | 'originalCurrentPriceCents'
  >,
): number => {
  const { holding } = subAccount;
  if (!holding) return 0;

  const isMultiCurrency = getIsMultiCurrencySubAccount(subAccount);

  const source = isMultiCurrency
    ? holding.originalValues?.find(
        (ov) => ov.currency === holding.financialProduct?.originalCurrency,
      )
    : (holding.originalValues?.find(
        (ov) => ov.currency === subAccount.accountBaseCurrency,
      ) ?? holding);

  const valueCents = source?.valueCents ?? 0;
  const quantity = source?.quantity ?? 0;
  const pendingQuantity = source?.pendingQuantity ?? 0;

  // guard from malformed data
  if (
    !Number.isFinite(valueCents) ||
    !Number.isFinite(quantity) ||
    !Number.isFinite(pendingQuantity)
  ) {
    return 0;
  }

  const settledQuantity = quantity - pendingQuantity;
  if (settledQuantity === 0) return 0;

  const pricePerShareCents =
    quantity !== 0
      ? valueCents / quantity
      : ((isMultiCurrency
          ? security?.originalCurrentPriceCents
          : security?.currentPriceCents) ?? 0);

  if (!Number.isFinite(pricePerShareCents)) return 0;

  return Math.round(settledQuantity * pricePerShareCents);
};

export const buildNewDraftTrade = (
  newAmountCents: number,
  subAccount: PortfolioSubAccount,
  security: PortfolioSecurity,
  currency: CurrencyCodes,
): DraftSubTradeRequest => {
  const currentAmountCents = getHoldingCurrentValueCents(subAccount);

  const amountCents = getAmountCents(newAmountCents, currentAmountCents);
  const type = getTradeType(newAmountCents, currentAmountCents);
  const isSellAll = checkIsSellAll(newAmountCents, currentAmountCents);

  return buildDraftTrade({
    financialProductId: security.id,
    subAccountId: subAccount.id,
    amountCents,
    currency: currency ?? CurrencyCodes.CAD,
    type,
    isSellAll,
    maxQuantity: subAccount?.holding?.quantity,
    ticker: security.ticker,
    accountName: subAccount.name,
  });
};

export const buildNewOptimalDraftTrade = (
  optimalHolding: OptimalHolding,
  subAccount: OptimalSubAccount,
  currency: CurrencyCodes,
  securities: PortfolioSecurity[],
  subAccounts: PortfolioSubAccount[],
  fxRate = 1,
): DraftSubTradeRequest | null => {
  const newAmountCents = round(
    subAccount.preTradeHoldingAllocations[optimalHolding.index] * fxRate,
    2,
  );

  if (newAmountCents === 0) return null;

  const type =
    newAmountCents > 0 ? SubTradeRequestTypes.Buy : SubTradeRequestTypes.Sell;
  const isSellAll = checkIsSellAllOptimal(
    newAmountCents,
    optimalHolding.valueCents,
  );
  const maxQuantity = subAccount.currentHoldingQuantities[optimalHolding.index];
  const ticker =
    securities.find((item) => item.id === optimalHolding.financialProduct)
      ?.ticker ?? '';
  const accountName =
    subAccounts.find((item) => item.id === subAccount.subAccount)?.name ?? '';

  return buildDraftTrade({
    financialProductId: optimalHolding.financialProduct,
    subAccountId: subAccount.subAccount,
    amountCents: newAmountCents,
    currency: currency ?? CurrencyCodes.CAD,
    type,
    isSellAll,
    maxQuantity,
    ticker,
    accountName,
  });
};

// Unique key for pending sub-trade maps: security-first (opposite of allocationCellKey).
export const holdingTradeKey = (securityId: string, subAccountId: string) =>
  `${securityId}_${subAccountId}`;

export const findPendingTrade = (
  key: string,
  maps: Map<string, DraftSubTradeRequest>[],
): DraftSubTradeRequest | null => {
  if (!maps.length) return null;

  for (const map of maps) {
    const trade = map.get(key);

    if (trade) return trade;
  }

  return null;
};

export const mergePendingTradesMaps = (
  maps: Map<string, DraftSubTradeRequest>[],
): Map<string, DraftSubTradeRequest> => {
  if (!maps.length) return new Map();

  // need a copy to avoid original array mutation
  const localMaps = maps.map((map) => new Map(map));

  const firstMap = localMaps.shift();

  const pendingTrades = new Map<string, DraftSubTradeRequest>(firstMap);

  for (const map of localMaps) {
    map.forEach((v, k) => pendingTrades.set(k, v));
  }

  return pendingTrades;
};

export const getPendingAmountCentsByKey = (
  key: string,
  maps: Map<string, DraftSubTradeRequest>[],
): number => findPendingTrade(key, maps)?.amountCents ?? 0;

export const isSellAllByKey = (
  key: string,
  maps: Map<string, DraftSubTradeRequest>[],
): boolean => findPendingTrade(key, maps)?.isSellAll ?? false;

export const getAmountFieldInputColorByKey = (
  key: string,
  pendingAmountCents: number,
  maps: Map<string, DraftSubTradeRequest>[],
  theme: Theme,
): string | undefined => {
  if (Math.abs(pendingAmountCents) <= 0) return theme.color.onSurface;

  const trade: DraftSubTradeRequest | null = findPendingTrade(key, maps);

  return (
    (trade?.type === SubTradeRequestTypes.Buy
      ? theme.color.positive
      : theme.color.negative) ?? theme.color.onSurface
  );
};

export const sumPendingTradesCentsByCondition = ({
  maps,
  callbackFn,
  currency = CurrencyCodes.CAD,
  fxRates = [],
}: {
  maps: Map<string, DraftSubTradeRequest>[];
  callbackFn?: (trade: DraftSubTradeRequest) => boolean;
  currency?: CurrencyCodes;
  fxRates?: FxRateDocument[];
}): number => {
  let sum = 0;

  const pendingTrades = mergePendingTradesMaps(maps);

  pendingTrades.forEach((trade) => {
    const fxRate = getFxRate(fxRates, trade.currency, currency);

    const amountCents = trade.amountCents * fxRate;

    if (callbackFn?.(trade)) sum += amountCents;

    if (!callbackFn) sum += amountCents;
  });

  return sum;
};

export const getSubAccountCurrentCents = ({
  subAccount,
  subAccountPendingTradesCents,
  originalCurrency,
  fxRate = 1,
}: {
  subAccount: PortfolioSubAccount;
  subAccountPendingTradesCents: number;
  originalCurrency?: CurrencyCodes;
  fxRate?: number;
}) => {
  let { cashCents, cashOnHoldToTradeCents } = subAccount;

  if (originalCurrency) {
    const originalValue = subAccount.originalValues?.find(
      (value) => value.currency === originalCurrency,
    );

    cashCents = originalValue?.cashCents ?? 0;
    cashOnHoldToTradeCents = originalValue?.cashOnHoldToTradeCents ?? 0;
  }

  const current =
    cashCents * fxRate -
    subAccountPendingTradesCents -
    cashOnHoldToTradeCents * fxRate;

  return Math.abs(Math.round(current)) === 0 ? 0 : current;
};

export const getSubAccountOriginalCurrentCents = (
  subAccount: PortfolioSubAccount,
  maps: Map<string, DraftSubTradeRequest>[],
) => {
  return subAccount?.originalValues?.map((originalValue) => {
    // Note: We filter for trades matching this specific currency AND pass the same currency
    // to avoid FX conversion (since all trades are already in the same currency)
    const originalValuePendingTradesCents = sumPendingTradesCentsByCondition({
      maps,
      callbackFn: (trade) =>
        trade.subAccountId === subAccount.id &&
        trade.currency === originalValue.currency,
      currency: originalValue.currency, // No FX conversion needed
      fxRates: [], // Empty array since we don't want any conversion
    });

    const originalValueCurrent = getSubAccountCurrentCents({
      subAccount,
      subAccountPendingTradesCents: originalValuePendingTradesCents,
      originalCurrency: originalValue.currency,
    });

    return {
      currency: originalValue.currency,
      current: originalValueCurrent,
    };
  });
};

export const sumSubAccountsCurrentCents = (
  subAccounts: PortfolioSubAccount[],
  maps: Map<string, DraftSubTradeRequest>[],
  currency: CurrencyCodes,
  fxRate: number,
  fxRates: FxRateDocument[],
): number =>
  subAccounts.reduce((prev: number, subAccount) => {
    const pendingTradesCents = sumPendingTradesCentsByCondition({
      maps,
      callbackFn: (trade) => trade.subAccountId === subAccount.id,
      currency,
      fxRates,
    });

    const current = getSubAccountCurrentCents({
      subAccount,
      subAccountPendingTradesCents: pendingTradesCents,
      fxRate,
    });

    return prev + current;
  }, 0);

export const sumSubAccountsCashOnHoldToTradeCents = (
  subAccounts: (PortfolioSubAccount | SubAccountReport)[] | undefined,
): number => {
  if (!subAccounts) {
    return 0; // Handle the case where subAccounts is undefined or null
  }

  return subAccounts.reduce((prev: number, subAccount) => {
    let cashOnHold = 0;
    if (
      'cashOnHoldToTradeCents' in subAccount &&
      subAccount.cashOnHoldToTradeCents !== undefined
    ) {
      cashOnHold = subAccount.cashOnHoldToTradeCents;
    }
    return prev + cashOnHold;
  }, 0);
};

export const sumSecuritiesCurrentCents = (
  securities: PortfolioSecurity[],
  maps: Map<string, DraftSubTradeRequest>[],
  currency: CurrencyCodes,
  fxRate: number,
  fxRates: FxRateDocument[],
): number =>
  securities?.reduce((prev: number, security) => {
    const pendingTradeCents = sumPendingTradesCentsByCondition({
      maps,
      callbackFn: (trade) => trade.financialProductId === security.id,
      currency,
      fxRates,
    });

    return prev + Math.round(security.valueCents * fxRate + pendingTradeCents);
  }, 0);

export const sumSecuritiesDeviationCents = (
  securities: PortfolioSecurity[],
  maps: Map<string, DraftSubTradeRequest>[],
  currency: CurrencyCodes,
  fxRate: number,
  fxRates: FxRateDocument[],
): number =>
  securities?.reduce((prev: number, security) => {
    const pendingTradesCents = sumPendingTradesCentsByCondition({
      maps,
      callbackFn: (trade) => trade.financialProductId === security.id,
      currency,
      fxRates,
    });

    return (
      prev +
      Math.round(security.valueCents * fxRate + pendingTradesCents) -
      Math.round(security.expectedValueCents * fxRate)
    );
  }, 0);

export const groupSecuritiesByAssetClass = (
  securities: PortfolioSecurity[],
  translateName: (translatedName: TranslatedString | undefined) => string,
  getUnassignedLabel: () => string,
): PortfolioAssetClassGroup[] => {
  const groupMap = new Map<string, PortfolioAssetClassGroup>();

  for (const s of securities) {
    const id = s.assetClass?.id ?? '__unassigned__';
    const name =
      translateName(s.assetClass?.translatedName) ||
      s.assetClass?.name ||
      getUnassignedLabel();
    // All securities in a group should report the same assetClassGroupPercentage.
    // We take the highest non-zero value seen so a stale 0 on one holding never
    // silently overwrites the real target percentage set by a sibling.
    const incomingPct = s.assetClassGroupPercentage ?? 0;

    const existing = groupMap.get(id);
    if (existing) {
      if (incomingPct > existing.groupPct) {
        existing.groupPct = incomingPct;
      }
      existing.items.push(s);
    } else {
      groupMap.set(id, {
        assetClassId: id,
        name,
        groupPct: incomingPct,
        items: [s],
      });
    }
  }

  return Array.from(groupMap.values());
};

export const sortSecurities = (a: PortfolioSecurity, b: PortfolioSecurity) => {
  const taxRankA = a.taxRank ?? 1000; // will be moved to the bottom and ordered by a ticker
  const taxRankB = b.taxRank ?? 1000;

  return taxRankA === taxRankB
    ? a.ticker.localeCompare(b.ticker)
    : taxRankA - taxRankB;
};

export const sortPortfolioReportSubAccounts = (
  a: PortfolioSubAccount,
  b: PortfolioSubAccount,
) => {
  if (a.taxRank !== b.taxRank) return a.taxRank - b.taxRank;
  const nameCompare = a.name.localeCompare(b.name);

  if (nameCompare !== 0) return nameCompare;
  return a.id.localeCompare(b.id);
};

export const areTradesAmountCentsDifferent = (
  draftTrades: Map<string, DraftSubTradeRequest>,
  initialDraftTrades: Map<string, DraftSubTradeRequest>,
): boolean => {
  // !todo: can't use the full potential of maps, iterators and generators in the current js version,
  // getting an error: Type 'IterableIterator<[string, DraftSubTradeRequest]>' can only be iterated through when using the '--downlevelIteration' flag or with a '--target' of 'es2015' or higher.
  const localDraftTradesToArray = Array.from(draftTrades.entries());

  for (const [key, trade] of localDraftTradesToArray) {
    const initialDraftTrade = initialDraftTrades.get(key);

    if (!initialDraftTrade) return true; // couldn't find a trade which means that the trade is a new one

    // !todo: should be updated when working with optimizer and splits as the amountCents will be different for a split
    if (initialDraftTrade.amountCents !== trade.amountCents) return true;
  }

  return false;
};

export const buildInitialTradeToVerifyState = (
  trade: SubTradeRequest,
): TradeToVerify => ({
  id: trade.id,
  selected: !!trade.verifiedAt && !!trade.verifiedBy,
  state: trade.state,
  verified: !!trade.verifiedAt,
});

export const collectPendingTransactionTickers = (
  subTradeRequest: any,
  usdTickers: string[],
  mfSellAllTickers: string[],
  organization: any,
) => {
  if (!organization?.allowPendingTransactions) return;
  if (
    subTradeRequest.financialProduct.currency === Currencies.Usd &&
    subTradeRequest.state === SubTradeRequestStates.Requested &&
    !usdTickers.includes(subTradeRequest.financialProduct.ticker)
  ) {
    usdTickers.push(subTradeRequest.financialProduct.ticker);
  }
  if (
    subTradeRequest.isSellAll === true &&
    subTradeRequest.state === SubTradeRequestStates.Requested &&
    subTradeRequest.financialProduct.exchange === Exchanges.Fundserv &&
    !mfSellAllTickers.includes(subTradeRequest.financialProduct.ticker)
  ) {
    mfSellAllTickers.push(subTradeRequest.financialProduct.ticker);
  }
};

/**
 * @warn the draftTrades map is mutated
 */
export const updateDraftTrades = (
  security: PortfolioSecurity,
  subAccount: PortfolioSubAccount,
  amount: string,
  currency: CurrencyCodes,
  draftTrades: Map<string, DraftSubTradeRequest>,
) => {
  const newAmount = dollars2cents(Number.parseFloat(amount) || 0, 2);

  const key = holdingTradeKey(security.id, subAccount.id);

  if (newAmount === getHoldingCurrentValueCents(subAccount)) {
    draftTrades.delete(key);

    return;
  }

  const newDraftTrade: DraftSubTradeRequest = buildNewDraftTrade(
    newAmount,
    subAccount,
    security,
    currency,
  );

  draftTrades.set(key, { ...draftTrades.get(key), ...newDraftTrade });
};

export const getSkeletonColumnsNumber = (conditions: boolean[], min: number) =>
  min + conditions.filter((condition) => condition).length;

export const getFxRate = (
  fxRates: FxRateDocument[],
  from: CurrencyCodes,
  to: CurrencyCodes,
) => {
  if (from === to) return 1;

  const directRate = fxRates?.find(
    (fxRate) => fxRate.from === from && fxRate.to === to,
  )?.rate;
  const inverseRate = fxRates?.find(
    (fxRate) => fxRate.from === to && fxRate.to === from,
  )?.rate;

  if (directRate) return directRate || 1;
  if (inverseRate) return 1 / inverseRate || 1;

  return 1;
};
